import { z } from 'zod';

import {
  type FeatureFlagControlFlagFieldsFragment,
  type FeatureFlagControlSegmentsQuery,
} from '../../../generated';
import {
  type ClauseValue,
  type ControlledFlag,
  type ControlledSegment,
  type FlagEvaluation,
  type FlagVariation,
  type TargetingClause,
  type TargetingRule,
} from '../types';

type GeneratedRule =
  FeatureFlagControlFlagFieldsFragment['environment']['rules'][number];

type GeneratedSegment = Extract<
  FeatureFlagControlSegmentsQuery['featureFlagControlSegments'],
  { __typename: 'FeatureFlagControlSegments' }
>['segments'][number];

type GeneratedSegmentRule = GeneratedSegment['rules'][number];

const zClauseValue = z.union([z.string(), z.number(), z.boolean()]);

/**
 * Decodes one AWSJSON value. Values that are not valid JSON are kept as the raw
 * string, so a clause still compares against what the provider stored.
 */
const decodeJson = (encoded: string): ClauseValue | null => {
  try {
    const parsed = zClauseValue.safeParse(JSON.parse(encoded));
    return parsed.success ? parsed.data : null;
  } catch {
    return encoded;
  }
};

const labelFor = (
  value: ClauseValue | null,
  encoded: string,
  name?: string | null
): string => {
  if (name) return name;
  if (value === true) return 'On';
  if (value === false) return 'Off';
  return value === null ? encoded : String(value);
};

const mapVariation = ({
  value,
  name,
}: FeatureFlagControlFlagFieldsFragment['variations'][number]): FlagVariation => {
  const decoded = decodeJson(value);
  return { label: labelFor(decoded, value, name), isOn: decoded === true };
};

const mapClauses = (clauses: GeneratedRule['clauses']): TargetingClause[] =>
  clauses.map((clause) => ({
    id: clause.id ?? undefined,
    attribute: clause.attribute,
    op: clause.op,
    negate: clause.negate,
    values: clause.values
      .map(decodeJson)
      .filter((value): value is ClauseValue => value !== null),
  }));

const mapRule = (rule: GeneratedRule): TargetingRule => ({
  id: rule.id ?? undefined,
  description: rule.description ?? undefined,
  disabled: rule.disabled,
  variation: rule.variation ?? undefined,
  isRollout: Boolean(rule.rollout),
  clauses: mapClauses(rule.clauses),
});

const mapSegmentRule = (rule: GeneratedSegmentRule): TargetingRule => ({
  id: rule.id ?? undefined,
  description: rule.description ?? undefined,
  disabled: false,
  isRollout: false,
  clauses: mapClauses(rule.clauses),
});

const mapEvaluation = (
  evaluation: FeatureFlagControlFlagFieldsFragment['evaluation']
): FlagEvaluation | undefined => {
  if (!evaluation?.value) return undefined;
  const decoded = decodeJson(evaluation.value);
  return {
    isOn: decoded === true,
    label: labelFor(decoded, evaluation.value),
    reason: evaluation.reason ?? undefined,
  };
};

export const mapFlag = (
  flag: FeatureFlagControlFlagFieldsFragment
): ControlledFlag => {
  const { environment } = flag;

  return {
    key: flag.key,
    name: flag.name,
    description: flag.description ?? undefined,
    tags: flag.tags,
    isBoolean: flag.kind === 'boolean',
    variations: flag.variations.map(mapVariation),
    enabled: environment.enabled,
    offVariation: environment.offVariation ?? undefined,
    fallthrough: {
      variation: environment.fallthrough?.variation ?? undefined,
      isRollout: Boolean(environment.fallthrough?.rollout),
    },
    targets: [...environment.targets, ...environment.contextTargets].map(
      (target) => ({
        variation: target.variation,
        values: target.values,
        contextKind: target.contextKind ?? undefined,
      })
    ),
    prerequisiteKeys: environment.prerequisites.map(({ key }) => key),
    rules: environment.rules.map(mapRule),
    evaluation: mapEvaluation(flag.evaluation),
  };
};

export const mapSegment = (segment: GeneratedSegment): ControlledSegment => ({
  key: segment.key,
  name: segment.name,
  unbounded: segment.unbounded,
  included: segment.included,
  excluded: segment.excluded,
  rules: segment.rules.map(mapSegmentRule),
});
